import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  UserPlus,
  Trash2,
  KeyRound,
  MoreHorizontal,
  ShieldCheck,
  UserCheck,
  UserX,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { systemUsersService, SystemUser } from '@/features/branding/services/system-users.service';
import { useSystemAuthStore } from '@/store/system-auth.store';

export const Route = createFileRoute('/system/users')({
  component: SystemUsersPage,
});

// ─── Dialog: Criar usuário ────────────────────────────────────────────────────

export function CreateUserDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');

  // Sempre abre limpo: descarta o rascunho ao fechar (cancelar, Esc, clique fora)
  useEffect(() => {
    if (!open) {
      setEmail('');
      setName('');
      setPassword('');
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => systemUsersService.create({ email, name: name || undefined, password }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-users'] });
      toast.success('Usuário criado com sucesso');
      setEmail('');
      setName('');
      setPassword('');
      onOpenChange(false);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message;
      toast.error(Array.isArray(msg) ? msg[0] : msg || 'Erro ao criar usuário');
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" busy={mutation.isPending} className="bg-gray-900 border-gray-700 text-gray-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-indigo-400" />
            Novo Usuário do Sistema
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (email && password) mutation.mutate();
          }}
        >
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="su-email" className="text-gray-300">Email *</Label>
            <Input
              id="su-email"
              type="email"
              placeholder="admin@plataforma.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-gray-800 border-gray-600 text-gray-100 placeholder:text-gray-500"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-gray-300">Nome</Label>
            <Input
              type="text"
              placeholder="João Silva"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-gray-800 border-gray-600 text-gray-100 placeholder:text-gray-500"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-gray-300">Senha *</Label>
            <Input
              type="password"
              placeholder="Mínimo 8 caracteres, maiúscula, número e especial"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-gray-800 border-gray-600 text-gray-100 placeholder:text-gray-500"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-gray-600 bg-transparent text-gray-300 hover:text-white"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={mutation.isPending}
            disabled={!email || !password}
            className="bg-indigo-600 hover:bg-indigo-500"
          >
            Criar usuário
          </Button>
        </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Dialog: Reset de senha ───────────────────────────────────────────────────

export function ResetPasswordDialog({
  user,
  open,
  onOpenChange,
}: {
  user: SystemUser | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    if (!open) setNewPassword('');
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => systemUsersService.resetPassword(user!.id, newPassword),
    onSuccess: () => {
      toast.success('Senha redefinida com sucesso');
      setNewPassword('');
      onOpenChange(false);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message;
      toast.error(Array.isArray(msg) ? msg[0] : msg || 'Erro ao redefinir senha');
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" busy={mutation.isPending} className="bg-gray-900 border-gray-700 text-gray-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-yellow-400" />
            Redefinir Senha
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-400">
          Usuário: <span className="text-gray-200 font-medium">{user?.email}</span>
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (newPassword) mutation.mutate();
          }}
        >
        <div className="space-y-1.5 py-2">
          <Label htmlFor="su-new-password" className="text-gray-300">Nova senha *</Label>
          <Input
            id="su-new-password"
            type="password"
            placeholder="Mínimo 8 caracteres, maiúscula, número e especial"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="bg-gray-800 border-gray-600 text-gray-100 placeholder:text-gray-500"
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-gray-600 bg-transparent text-gray-300 hover:text-white"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={mutation.isPending}
            disabled={!newPassword}
            className="bg-yellow-600 hover:bg-yellow-500"
          >
            Redefinir senha
          </Button>
        </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

function SystemUsersPage() {
  const { user: currentUser } = useSystemAuthStore();
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<SystemUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SystemUser | null>(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['system-users'],
    queryFn: () => systemUsersService.list(),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      systemUsersService.update(id, { active }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['system-users'] });
      toast.success(vars.active ? 'Usuário ativado' : 'Usuário desativado');
    },
    onError: () => toast.error('Erro ao atualizar status'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => systemUsersService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-users'] });
      toast.success('Usuário excluído');
      setDeleteTarget(null);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message;
      toast.error(msg || 'Erro ao excluir usuário');
    },
  });

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Usuários da Plataforma</h1>
          <p className="text-sm text-gray-400 mt-1">
            Administradores com acesso ao painel da plataforma
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-indigo-600 hover:bg-indigo-500 gap-2"
        >
          <UserPlus className="h-4 w-4" />
          Novo Usuário
        </Button>
      </div>

      {/* Tabela */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm text-gray-400 font-medium">
            {users.length} usuário{users.length !== 1 ? 's' : ''} cadastrado{users.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center text-gray-500 py-8">Carregando...</p>
          ) : users.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Nenhum usuário cadastrado</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800 text-gray-400">
                    <th className="text-left pb-3 pr-4">Email</th>
                    <th className="text-left pb-3 pr-4">Nome</th>
                    <th className="text-left pb-3 pr-4">Status</th>
                    <th className="text-left pb-3 pr-4">Criado em</th>
                    <th className="text-right pb-3">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {users.map((user) => (
                    <tr key={user.id} className="group">
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-100 font-medium">{user.email}</span>
                          {currentUser?.id === user.id && (
                            <Badge className="bg-indigo-900 text-indigo-300 text-xs px-1.5">você</Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-gray-400">
                        {user.name ?? <span className="text-gray-600 italic">—</span>}
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <Switch
                            aria-label={`Ativar ${user.email}`}
                            checked={user.active}
                            disabled={currentUser?.id === user.id || toggleMutation.isPending}
                            onCheckedChange={(checked) =>
                              toggleMutation.mutate({ id: user.id, active: checked })
                            }
                            className="data-[state=checked]:bg-emerald-600"
                          />
                          <span className={user.active ? 'text-emerald-400' : 'text-gray-500'}>
                            {user.active ? (
                              <span className="flex items-center gap-1">
                                <UserCheck className="h-3.5 w-3.5" /> Ativo
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <UserX className="h-3.5 w-3.5" /> Inativo
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-gray-400 text-xs">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Ações de ${user.email}`}
                              className="h-8 w-8 text-gray-500 hover:text-white opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 transition-opacity"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="bg-gray-800 border-gray-700 text-gray-100"
                          >
                            <DropdownMenuItem
                              onClick={() => setResetTarget(user)}
                              className="gap-2 focus:bg-gray-700 cursor-pointer"
                            >
                              <KeyRound className="h-4 w-4 text-yellow-400" />
                              Redefinir senha
                            </DropdownMenuItem>
                            {currentUser?.id !== user.id && (
                              <>
                                <DropdownMenuSeparator className="bg-gray-700" />
                                <DropdownMenuItem
                                  onClick={() => setDeleteTarget(user)}
                                  className="gap-2 text-red-400 focus:bg-gray-700 focus:text-red-400 cursor-pointer"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Excluir usuário
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} />

      <ResetPasswordDialog
        user={resetTarget}
        open={!!resetTarget}
        onOpenChange={(v) => !v && setResetTarget(null)}
      />

      {/* Confirm delete dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent size="sm" busy={deleteMutation.isPending} className="bg-gray-900 border-gray-700 text-gray-100">
          <DialogHeader>
            <DialogTitle className="text-red-400">Excluir usuário?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-400 leading-relaxed">
            O usuário{' '}
            <span className="text-gray-200 font-medium">{deleteTarget?.email}</span> será
            permanentemente removido da plataforma. Esta ação não pode ser desfeita.
          </p>
          <DialogFooter className="mt-2">
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              className="border-gray-600 bg-transparent text-gray-300 hover:text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              loading={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              Excluir usuário
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
